import { Text, ToggleButton, makeStyles, tokens } from "@fluentui/react-components";
import { WEEKDAYS, type ClientPreferenceInput, type Weekday } from "@gymplan/shared";
import type { AvailabilitySlot } from "../lib/api.ts";
import { WEEKDAY_LABEL } from "../lib/format.ts";

/** Phone-friendly preference editor: per day, tap the parts of the day that work. */
export const PARTS = [
  { key: "morning", label: "Morning", start: "06:00", end: "12:00" },
  { key: "lunch", label: "Lunch", start: "12:00", end: "14:00" },
  { key: "afternoon", label: "Afternoon", start: "14:00", end: "17:00" },
  { key: "evening", label: "Evening", start: "17:00", end: "21:00" },
] as const;
type PartKey = (typeof PARTS)[number]["key"] | "any";

const useStyles = makeStyles({
  day: { display: "flex", flexDirection: "column", gap: "6px", padding: "10px 0", borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  head: { display: "flex", alignItems: "baseline", gap: "8px" },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px" },
  off: { opacity: 0.5 },
});

const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const overlaps = (a: { start: string; end: string }, b: { startTime: string; endTime: string }) => toMin(a.start) < toMin(b.endTime) && toMin(b.startTime) < toMin(a.end);

export function DayChips({ value, onChange, availability = [] }: { value: ClientPreferenceInput[]; onChange: (v: ClientPreferenceInput[]) => void; availability?: AvailabilitySlot[] }) {
  const s = useStyles();

  const selected = (wd: Weekday): Set<PartKey> => {
    const out = new Set<PartKey>();
    for (const p of value.filter((x) => x.weekday === wd)) {
      if (!p.startTime) out.add("any");
      else {
        const part = PARTS.find((q) => q.start === p.startTime && q.end === p.endTime);
        if (part) out.add(part.key);
      }
    }
    return out;
  };

  const toggle = (wd: Weekday, key: PartKey) => {
    const others = value.filter((x) => x.weekday !== wd);
    const mine = selected(wd);
    if (key === "any") {
      onChange(mine.has("any") ? others : [...others, { weekday: wd, gymIds: [] }]);
      return;
    }
    const next = new Set(mine);
    next.delete("any");
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange([...others, ...custom(wd), ...PARTS.filter((p) => next.has(p.key)).map((p) => ({ weekday: wd, startTime: p.start, endTime: p.end, gymIds: [] }))]);
  };

  /** Exact times set another way (e.g. "not before 8:30am" in the sign-up chat): shown as their own chips. */
  const custom = (wd: Weekday) => value.filter((x) => x.weekday === wd && x.startTime && !PARTS.some((q) => q.start === x.startTime && q.end === x.endTime));
  const dropCustom = (p: ClientPreferenceInput) => onChange(value.filter((x) => x !== p));

  return (
    <div>
      {WEEKDAYS.map((wd) => {
        const avail = availability.filter((a) => a.weekday === wd);
        const trainerOff = availability.length > 0 && avail.length === 0;
        const sel = selected(wd);
        return (
          <div key={wd} className={`${s.day} ${trainerOff ? s.off : ""}`}>
            <div className={s.head}>
              <Text weight="semibold">{WEEKDAY_LABEL[wd]}</Text>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                {trainerOff ? "trainer not available" : avail.length ? `trainer: ${avail.map((a) => `${a.startTime}–${a.endTime}`).join(", ")}` : ""}
              </Text>
            </div>
            {!trainerOff && (
              <div className={s.chips}>
                <ToggleButton size="small" shape="circular" checked={sel.has("any")} onClick={() => toggle(wd, "any")}>
                  Any time
                </ToggleButton>
                {custom(wd).map((p) => (
                  <ToggleButton key={`${p.startTime}-${p.endTime}`} size="small" shape="circular" checked onClick={() => dropCustom(p)} title="Tap to remove">
                    {p.startTime}–{p.endTime}
                  </ToggleButton>
                ))}
                {PARTS.filter((p) => avail.length === 0 || avail.some((a) => overlaps(p, a))).map((p) => (
                  <ToggleButton key={p.key} size="small" shape="circular" checked={sel.has(p.key)} onClick={() => toggle(wd, p.key)}>
                    {p.label}
                  </ToggleButton>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
